/**
 * Геометрия кадра и охранные зоны TikTok - ОДНО место на весь продукт.
 *
 * До Фазы 4 значения охранных зон лежали module-local константами внутри
 * `apps/overlay/src/Watermark.tsx` без экспорта. Это работало ровно до
 * появления второго потребителя, и второй потребитель - редактор раскладки в
 * dashboard - не может импортировать из `apps/overlay` вовсе. Копия зон в
 * редакторе разошлась бы с копией в overlay молча, и стример, поставивший
 * таблицу по границе зоны в редакторе, увидел бы её под чатом в эфире.
 *
 * Пакет выбран `game-sdk`, а не `protocol`: это не форма сообщения, а
 * геометрия сцены, и она уже наполовину здесь - `SceneConfig.width/height`.
 */
/** Логическая ширина кадра. Константа, а не настройка (ARCHITECTURE 5.1). */
export const FRAME_WIDTH = 1080;
/** Логическая высота кадра. */
export const FRAME_HEIGHT = 1920;
/**
 * Охранные зоны интерфейса TikTok, доли от размера кадра.
 *
 * Самый серьёзный пробел раздела 5.2: конкурент не показывает их НИГДЕ - ни
 * правила в стилях, ни упоминания в интерфейсе, ни строки в документации.
 * Косвенный учёт зашит в код его игр отдельными числами на игру
 * (`centerOffsetY`: +268 у одной, -140 у другой, +620 у легенды третьей), а
 * стример, двигающий элементы сам, не имеет ни малейшего ориентира.
 *
 * ЭТО ПРЕДУПРЕЖДЕНИЕ, А НЕ ЗАПРЕТ. Элемент можно поставить в зону осознанно -
 * продукт обязан только сообщить.
 *
 * Правая колонка не замерена на живом эфире: 15 % взяты с запасом по
 * известной раскладке LIVE-интерфейса (`DECISIONS.md`, [2026-09-17]). Замер
 * назначен; исходное положение обязано быть безопасным ДО замера, а не после
 * жалобы.
 */
export const TIKTOK_GUARD = {
    /** Верх: имя канала, счётчик зрителей, кнопка выхода. */
    top: 0.17,
    /** Низ: лента комментариев, поле ввода, панель подарков. */
    bottom: 0.22,
    /** Правая колонка кнопок: подарки, поделиться, лайк, меню. */
    right: 0.15,
    /** Слева интерфейс TikTok кадр не перекрывает. */
    left: 0,
};
/**
 * Watermark платформы стоит ВНУТРИ охранной зоны TikTok.
 *
 * РЕШЕНИЕ ВЛАДЕЛЬЦА И ЕГО ЦЕНА. Watermark выведен из безопасной области и не
 * участвует в раскладке содержимого: игровое поле, легенда, каллауты и HUD
 * получают всю безопасную область целиком, конкуренция за место снята.
 *
 * Цена принята осознанно: в охранной зоне метку закрывает интерфейс TikTok,
 * то есть зритель её практически не видит. Watermark теряет обе свои функции -
 * узнаваемость платформы у чужой аудитории и давление на переход к Pro.
 * Подробности и условия пересмотра - `docs/DECISIONS.md`, запись [2026-09-17]
 * «Watermark переносится в охранную зону».
 *
 * ПОЧЕМУ НИЖНЯЯ ЗОНА. Из трёх она перекрыта наиболее полно: `OVERLAY_LAYOUT.md`,
 * 5.1 - «нижние примерно 400 пикселей закрыты лентой комментариев, правый край -
 * колонкой кнопок, а верх - шапкой эфира». Лента комментариев идёт во всю
 * ширину и непрерывно обновляется; колонка кнопок справа - отдельные значки,
 * между которыми содержимое видно, а шапка сверху ниже и разреженнее. То есть
 * при иных пропорциях кадра метка с меньшей вероятностью окажется поверх
 * содержимого именно снизу.
 *
 * Косвенное подтверждение: у конкурента watermark на бесплатном тарифе прибит
 * к правому нижнему углу (`OVERLAY_LAYOUT.md`, 1.6) - туда же, куда телеканалы
 * ставят логотип.
 */
export const WATERMARK_BOX = {
    /** Высота плашки в логических пикселях, с запасом на кромку. */
    height: 80,
    /** Ширина плашки с запасом: она растёт от правого края влево. */
    width: 320,
    /** Отступ от краёв кадра. */
    inset: 24,
};
/**
 * Прямоугольник watermark в логических пикселях.
 *
 * Лежит целиком в нижней охранной зоне и НЕ ПЕРЕСЕКАЕТСЯ с безопасной
 * областью - это проверяется тестом, а не подразумевается.
 */
export function watermarkBox() {
    const bottom = FRAME_HEIGHT - WATERMARK_BOX.inset;
    const right = FRAME_WIDTH - WATERMARK_BOX.inset;
    return {
        top: bottom - WATERMARK_BOX.height,
        bottom,
        left: right - WATERMARK_BOX.width,
        right,
    };
}
/** Безопасная область кадра в логических пикселях. */
export function safeArea() {
    const top = FRAME_HEIGHT * TIKTOK_GUARD.top;
    const bottom = FRAME_HEIGHT * (1 - TIKTOK_GUARD.bottom);
    const left = FRAME_WIDTH * TIKTOK_GUARD.left;
    const right = FRAME_WIDTH * (1 - TIKTOK_GUARD.right);
    return { top, bottom, left, right, width: right - left, height: bottom - top };
}
/** Попадает ли прямоугольник элемента в охранную зону. Для предупреждений. */
export function overlapsGuard(box) {
    const safe = safeArea();
    return (box.y < safe.top ||
        box.y + box.height > safe.bottom ||
        box.x < safe.left ||
        box.x + box.width > safe.right);
}
/**
 * Вписывание кадра 1080x1920 в окно browser source.
 *
 * Формула снята с живого overlay конкурента и воспроизводится точно до
 * седьмого знака на трёх разных размерах окна (`OVERLAY_LAYOUT.md`, 4.1).
 */
export function computeFrameTransform(width, height) {
    const scale = Math.min(width / FRAME_WIDTH, height / FRAME_HEIGHT);
    return {
        scale,
        translateX: (width - FRAME_WIDTH * scale) / 2,
        translateY: (height - FRAME_HEIGHT * scale) / 2,
    };
}
//# sourceMappingURL=frame.js.map